import React, { useState, useEffect } from "react";
import style from "./demandNoteConfig.module.css";
import { useNavigate } from "react-router-dom";
import { ErrorToast, SuccessToast } from "../../../components/toastMessage";
import { BASE_URL } from "../../../constants/Constants";
import BoxComponent from "../../../components/CommonComponents/BoxComponent/BoxComponent";
import { _axios } from "../../../utils/axios";

interface DemandNoteConfigState {
  rr_land_rate: string;  // changed
  rr_land_rate_commercial: string;   // new
  rr_land_rate_industrial: string;
  rr_construction_rate: string;
}

const DemandNoteConfig = () => {
  const [existenceData, setExistenceData] = useState(false);
  const navigate = useNavigate();

  const [demandNoteConfig, setDemandNoteConfig] =
    useState<DemandNoteConfigState>({
      rr_land_rate: "",
      rr_land_rate_commercial: "",   // new
      rr_land_rate_industrial: "",
      rr_construction_rate: "",
    });

  const fetchDemandNoteConfig = async () => {
    try {
      const existenceResponse = await _axios.get(
        `${BASE_URL}/api/demand-note-config/check-existence`
      );
      setExistenceData(existenceResponse.data.exists);

      if (existenceResponse.data.exists) {
        const configResponse = await _axios.get(
          `${BASE_URL}/api/demand-note-config/get-config`
        );
        if (configResponse.data.success && configResponse.data.data) {
          setDemandNoteConfig({
            rr_land_rate: configResponse.data.data.rr_land_rate,
            rr_land_rate_commercial: configResponse.data.data.rr_land_rate_commercial,  // new
            rr_land_rate_industrial: configResponse.data.data.rr_land_rate_industrial,            rr_construction_rate: configResponse.data.data.rr_construction_rate,
          });
        }
      }
    } catch (error) {
      console.error("Fetch error:", error);
      ErrorToast("Failed to load configuration");
    }
  };

  useEffect(() => {
    fetchDemandNoteConfig();
  }, []);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;

    // Allow only numbers and decimal point
    const numericValue = value.replace(/[^0-9.]/g, "");

    // Ensure only one decimal point
    const parts = numericValue.split(".");
    if (parts.length === 2 && parts[1].length > 2) return;

    if (parts.length > 2) return;

    setDemandNoteConfig((prev) => ({
      ...prev,
      [name]: numericValue,
    }));
  };

  const formatCurrency = (value: string) => {
    if (!value) return "";
    const numericValue = parseFloat(value);
    if (isNaN(numericValue)) return value;
    return numericValue.toLocaleString("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    // Convert to numbers and validate
    const landRate  = parseFloat(demandNoteConfig.rr_land_rate);
    const landRateCommercial = parseFloat(demandNoteConfig.rr_land_rate_commercial);   // new
    const landRateIndustrial = parseFloat(demandNoteConfig.rr_land_rate_industrial);
    const constructionRate = parseFloat(demandNoteConfig.rr_construction_rate);

    if (isNaN(landRate) || isNaN(landRateCommercial) ||
        isNaN(landRateIndustrial) || isNaN(constructionRate)) {
        ErrorToast("All fields are required. Please enter numeric values (0 or greater)");      return;
    }
/*
    if (landRate <= 0 ||
        landRateCommercial <= 0 ||
        landRateIndustrial <= 0 ||
        constructionRate <= 0) {
      ErrorToast("Rates must be greater than 0");
      return;
    }
*/
    try {
        const configData = {
            rr_land_rate: parseFloat(landRate.toFixed(2)),
            rr_land_rate_commercial: parseFloat(landRateCommercial.toFixed(2)),
            rr_land_rate_industrial: parseFloat(landRateIndustrial.toFixed(2)),
            rr_construction_rate: parseFloat(constructionRate.toFixed(2)),
        };

        if (!existenceData) {
        const response = await _axios.post(
          `${BASE_URL}/api/demand-note-config/create`,
          configData
        );
        if (response.data.success) {
          SuccessToast("Configuration created successfully");
          fetchDemandNoteConfig();
        }
      } else {
        const response = await _axios.patch(
          `${BASE_URL}/api/demand-note-config/update`,
          configData
        );
        if (response.data.success) {
          SuccessToast("Configuration updated successfully");
          fetchDemandNoteConfig();
        }
      }
    } catch (error) {
      console.error("Save error:", error);
      ErrorToast("Failed to save configuration");
    }
  };

  return (
      <div className={`${style.body} p-8`}>
        <p className={style.bodyTitleNormal}>Demand Note Configurations</p>
        <BoxComponent>
          <div className={style.bodyContanier}>
            <div className={style.logoUploadContainer}>
              <div className={style.uploadsection}>
                <form onSubmit={handleSubmit}>
                  {/* FIRST ROW - Residential and Construction Rate */}
                  <div className={style.formRow}>
                    <div className={style.formGroup}>
                      <label className={style.subHeadingLabel}>
                        R.R. Land Rate For Residential <span className="ml-1 text-danger">*</span>
                      </label>
                      <div className={style.currencyContainer}>
                        <span className={style.currencySymbol}>₹</span>
                        <input
                            className={style.InputTxtNum}
                            type="text"
                            name="rr_land_rate"
                            value={demandNoteConfig.rr_land_rate}
                            onChange={handleChange}
                            placeholder="0.00"
                            onKeyPress={(e) => {
                              if (
                                  !/[0-9.]/.test(e.key) &&
                                  ![
                                    "Backspace",
                                    "Delete",
                                    "ArrowLeft",
                                    "ArrowRight",
                                    "Tab",
                                  ].includes(e.key)
                              ) {
                                e.preventDefault();
                              }
                            }}
                        />
                      </div>
                    </div>

                    <div className={style.formGroup}>
                      <label className={style.subHeadingLabel}>
                        R.R Construction Rate{" "}
                        <span className="ml-1 text-danger">*</span>
                      </label>
                      <div className={style.currencyContainer}>
                        <span className={style.currencySymbol}>₹</span>
                        <input
                            className={style.InputTxtNum}
                            type="text"
                            name="rr_construction_rate"
                            value={demandNoteConfig.rr_construction_rate}
                            onChange={handleChange}
                            placeholder="0.00"
                            onKeyPress={(e) => {
                              if (
                                  !/[0-9.]/.test(e.key) &&
                                  ![
                                    "Backspace",
                                    "Delete",
                                    "ArrowLeft",
                                    "ArrowRight",
                                    "Tab",
                                  ].includes(e.key)
                              ) {
                                e.preventDefault();
                              }
                            }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* SECOND ROW - Commercial and Industrial */}
                  <div className={style.formRow}>
                    <div className={style.formGroup}>
                      <label className={style.subHeadingLabel}>
                        R. R. Land Rate For Commercial <span className="ml-1 text-danger">*</span>
                      </label>
                      <div className={style.currencyContainer}>
                        <span className={style.currencySymbol}>₹</span>
                        <input
                            className={style.InputTxtNum}
                            type="text"
                            name="rr_land_rate_commercial"
                            value={demandNoteConfig.rr_land_rate_commercial}
                            onChange={handleChange}
                            placeholder="0.00"
                            onKeyPress={(e) => {
                              if (
                                  !/[0-9.]/.test(e.key) &&
                                  ![
                                    "Backspace",
                                    "Delete",
                                    "ArrowLeft",
                                    "ArrowRight",
                                    "Tab",
                                  ].includes(e.key)
                              ) {
                                e.preventDefault();
                              }
                            }}
                        />
                      </div>
                    </div>

                    <div className={style.formGroup}>
                      <label className={style.subHeadingLabel}>
                        R. R. Land Rate For Industrial <span className="ml-1 text-danger">*</span>
                      </label>
                      <div className={style.currencyContainer}>
                        <span className={style.currencySymbol}>₹</span>
                        <input
                            className={style.InputTxtNum}
                            type="text"
                            name="rr_land_rate_industrial"
                            value={demandNoteConfig.rr_land_rate_industrial}
                            onChange={handleChange}
                            placeholder="0.00"
                            onKeyPress={(e) => {
                              if (
                                  !/[0-9.]/.test(e.key) &&
                                  ![
                                    "Backspace",
                                    "Delete",
                                    "ArrowLeft",
                                    "ArrowRight",
                                    "Tab",
                                  ].includes(e.key)
                              ) {
                                e.preventDefault();
                              }
                            }}
                        />
                      </div>
                    </div>
                  </div>

                  <div className={style.buttonSection}>
                    <button
                        type="submit"
                        className="bg-primary ml-4 px-6 py-2 font-bold text-white rounded-full hover:bg-primary-dark"
                    >
                      Save
                    </button>
                    <button
                        type="button"
                        className="bg-black ml-4 px-6 py-2 font-bold text-white rounded-full hover:bg-gray-800"
                        onClick={() => navigate("/dashboard")}
                    >
                      Close
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </BoxComponent>
      </div>
  );
};

export default DemandNoteConfig;
